// src/pages/ModuleLessonPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { supabase } from '../supabaseClient';

const API_BASE = process.env.REACT_APP_API_URL;

function Section({ title, children, right }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function Pill({ children, className = '' }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${className}`}>
      {children}
    </span>
  );
}

function LevelBadge({ level }) {
  const lv = (level || 'beginner').toLowerCase();
  const cls =
    lv === 'advanced'
      ? 'bg-red-100 text-red-800 border-red-200'
      : lv === 'intermediate'
      ? 'bg-amber-100 text-amber-800 border-amber-200'
      : 'bg-emerald-100 text-emerald-800 border-emerald-200';
  const label = lv[0].toUpperCase() + lv.slice(1);
  return <Pill className={cls}>{label}</Pill>;
}

export default function ModuleLessonPage() {
  const { lessonId, moduleId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [lesson, setLesson] = useState(null);
  const [answers, setAnswers] = useState({});
  const [writing, setWriting] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  // Safe quiz parser (handles jsonb or string)
  const quiz = useMemo(() => {
    if (!lesson?.quiz) return [];
    try {
      if (Array.isArray(lesson.quiz)) return lesson.quiz;
      if (typeof lesson.quiz === 'string') {
        const obj = JSON.parse(lesson.quiz);
        return Array.isArray(obj) ? obj : Array.isArray(obj?.questions) ? obj.questions : [];
      }
      // object with questions
      if (Array.isArray(lesson.quiz?.questions)) return lesson.quiz.questions;
      return [];
    } catch {
      return [];
    }
  }, [lesson]);

  useEffect(() => {
    const fetchAll = async () => {
      if (!lessonId) return;
      setLoading(true);
      setLoadError('');
      try {
        const [{ data: lessonRow, error: lessonErr }] = await Promise.all([
          supabase.from('module_lessons').select('*').eq('id', lessonId).maybeSingle(),
        ]);
        if (lessonErr || !lessonRow) {
          throw new Error(lessonErr?.message || 'Lesson not found');
        }
        setLesson(lessonRow);

        if (user && moduleId) {
          const { data: prog, error: progErr } = await supabase
            .from('module_progress')
            .select('*')
            .eq('user_id', user.id)
            .eq('module_id', moduleId)
            .eq('lesson_id', lessonId)
            .maybeSingle();

          if (!progErr && prog) {
            setAnswers(prog.quiz_answers || {});
            setWriting(prog.writing_response || '');
            setFeedback(prog.feedback || null);
            setScore(typeof prog.score === 'number' ? prog.score : null);
          }
        }
      } catch (e) {
        setLoadError(e.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [lessonId, moduleId, user]);

  const handleChange = (qIdx, opt) => {
    setAnswers((prev) => ({ ...prev, [qIdx]: opt }));
  };

  const computeQuizScore = () => {
    if (!quiz.length) return { correct: 0, total: 0, pct: null };
    let correct = 0;
    quiz.forEach((q, idx) => {
      if (answers[idx] && q.correct && answers[idx] === q.correct) correct += 1;
    });
    const total = quiz.length;
    const pct = Math.round((correct / total) * 100);
    return { correct, total, pct };
  };

  const handleSubmit = async () => {
    if (!lesson) return;
    setSubmitting(true);
    try {
      let fb = null;
      let finalScore = null;

      // If there is a writing prompt and student wrote something, use the AI score
      if (lesson.allow_writing && (writing || '').trim().length > 0) {
        const res = await fetch(`${API_BASE}/api/lesson-feedback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt: lesson.writing_prompt || '',
            input: writing || '',
          }),
        });
        const data = await res.json();
        fb = data?.feedback ?? null;
        // Normalize: ensure numeric score 0-100 if present
        if (typeof data?.score === 'number') {
          finalScore = Math.max(0, Math.min(100, Math.round(data.score)));
        }
      }

      // If no writing score, fall back to quiz score if a quiz exists
      if (finalScore === null) {
        const { pct } = computeQuizScore();
        if (typeof pct === 'number') finalScore = pct;
      }

      // Save progress
      await supabase.from('module_progress').upsert(
        {
          user_id: user.id,
          module_id: moduleId,
          lesson_id: lessonId,
          completed: true,
          quiz_answers: answers,
          writing_response: writing,
          feedback: fb,
          score: finalScore,
          updated_at: new Date().toISOString(),
        },
        { onConflict: ['user_id', 'module_id', 'lesson_id'] }
      );

      setFeedback(fb);
      setScore(finalScore);
    } catch (err) {
      console.error('Failed to save module lesson progress:', err);
      alert('Error saving your progress.');
    } finally {
      setSubmitting(false);
    }
  };

  // Derived UI helpers
  const { correct, total, pct } = computeQuizScore();

  if (!user) return null;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <div className="h-6 bg-gray-100 rounded w-2/3 animate-pulse" />
        <div className="h-24 bg-gray-100 rounded animate-pulse" />
        <div className="h-10 bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  if (loadError || !lesson) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <p className="text-red-600">{loadError || 'Lesson not found.'}</p>
        <div className="mt-4">
          <Link to={`/modules/view/${moduleId}`} className="text-blue-600 hover:underline">
            Back to module
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="text-2xl font-bold text-gray-900">{lesson.title}</h2>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
          <LevelBadge level={lesson.level} />
          {lesson.allow_writing && (
            <Pill className="bg-purple-50 text-purple-700 border-purple-200">Writing</Pill>
          )}
          {lesson.allow_case_builder && (
            <Pill className="bg-rose-50 text-rose-700 border-rose-200">Case Builder</Pill>
          )}
          {Array.isArray(quiz) && quiz.length > 0 && (
            <Pill className="bg-blue-50 text-blue-700 border-blue-200">{quiz.length} quiz question{quiz.length === 1 ? '' : 's'}</Pill>
          )}
          {typeof score === 'number' && (
            <Pill className="bg-emerald-50 text-emerald-700 border-emerald-200">
              Score: {score}%
            </Pill>
          )}
        </div>
      </div>

      {/* Reading */}
      <Section title="Reading">
        <p className="whitespace-pre-line text-gray-800 leading-relaxed">
          {lesson.reading || 'No reading provided for this lesson.'}
        </p>
      </Section>

      {/* Quiz */}
      {quiz.length > 0 && (
        <Section
          title="Quiz"
          right={
            typeof pct === 'number' && score !== null ? (
              <span className="text-sm text-gray-700">
                Your quiz score: <strong>{correct}/{total}</strong> ({pct}%)
              </span>
            ) : null
          }
        >
          <div className="space-y-5">
            {quiz.map((q, idx) => (
              <div key={idx} className="rounded-md border border-gray-200 p-4">
                <p className="font-medium text-gray-900">{q.text}</p>
                <div className="mt-2 space-y-1">
                  {(q.options || []).map((opt, i) => {
                    const checked = answers[idx] === opt;
                    const showKey = score !== null; // reveal answers after submit
                    const isCorrect = showKey && q.correct === opt;
                    const isChosenWrong = showKey && checked && q.correct !== opt;

                    return (
                      <label
                        key={i}
                        className={`block rounded px-2 py-1 cursor-pointer ${
                          isCorrect
                            ? 'bg-emerald-50 border border-emerald-200'
                            : isChosenWrong
                            ? 'bg-red-50 border border-red-200'
                            : 'border border-transparent hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`q-${idx}`}
                          value={opt}
                          checked={checked}
                          onChange={() => handleChange(idx, opt)}
                          className="mr-2"
                          disabled={false}
                        />
                        {opt}
                      </label>
                    );
                  })}
                </div>
                {score !== null && (
                  <p className="mt-2 text-sm text-gray-600">
                    Correct answer: <strong>{q.correct}</strong>
                  </p>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Writing */}
      {lesson.allow_writing && (
        <Section title="Writing Prompt">
          {lesson.writing_prompt ? (
            <p className="mb-2 text-gray-800">{lesson.writing_prompt}</p>
          ) : (
            <p className="mb-2 text-gray-500">No specific prompt provided.</p>
          )}
          <textarea
            rows={6}
            value={writing}
            onChange={(e) => setWriting(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
            placeholder="Write your response here..."
          />
          {score !== null && (
            <div className="mt-3 text-sm text-gray-700 space-y-1">
              <p><strong>Score:</strong> {score}%</p>
              {feedback && (
                <p className="whitespace-pre-wrap">
                  <strong>Feedback:</strong> {feedback}
                </p>
              )}
            </div>
          )}
        </Section>
      )}

      {/* Case Builder */}
      {lesson.allow_case_builder && (
        <Section title="Case Builder (optional)">
          {lesson.case_builder_prompt ? (
            <p className="text-gray-800 whitespace-pre-line">{lesson.case_builder_prompt}</p>
          ) : (
            <p className="text-gray-500">No case builder prompt provided for this lesson.</p>
          )}
        </Section>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Link
          to={`/modules/view/${moduleId}`}
          className="text-gray-700 hover:underline"
        >
          ← Back to module
        </Link>

        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
        >
          {submitting ? 'Saving…' : score !== null ? 'Resubmit' : 'Submit'}
        </button>
      </div>
    </div>
  );
}
